import { useEffect, useState } from "react";

import { loadProfiles } from "../../data/firestore";
import { verifyPassword } from "../../utils/passwordHash";

import { ProfileCard } from "./ProfileCard";
import { ProfileLockDialog } from "./ProfileLockDialog";

import "./ProfileSelector.scss";

import type { Profile } from "../../types/profile";

interface ProfileSelectorProps {
  onProfileUnlocked: (profile: Profile) => void;
}

export const ProfileSelector = ({
  onProfileUnlocked,
}: ProfileSelectorProps) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfiles()
      .then((loadedProfiles) => {
        setProfiles(loadedProfiles);
      })
      .catch((err) => console.error("Failed to load profiles:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleProfileSelect = (profile: Profile) => {
    // Check if this profile is remembered
    const isAuthenticated =
      localStorage.getItem(`profile_auth_${profile.id}`) === "true";
    const expiresStr = localStorage.getItem(
      `profile_auth_${profile.id}_expires`,
    );

    if (isAuthenticated && expiresStr) {
      const expires = parseInt(expiresStr, 10);
      if (Date.now() < expires) {
        // Skip password dialog and unlock immediately
        onProfileUnlocked(profile);
        return;
      }
      // Expired, remove from localStorage
      localStorage.removeItem(`profile_auth_${profile.id}`);
      localStorage.removeItem(`profile_auth_${profile.id}_expires`);
    }

    // Not remembered or expired, show password dialog
    setSelectedProfile(profile);
    setError("");
  };

  const handleUnlock = async (password: string, rememberMe: boolean) => {
    if (!selectedProfile) {
      return;
    }

    // Verify password hash
    const isValid = await verifyPassword(password, selectedProfile.password);
    if (isValid) {
      // Store remember me only if checkbox was checked AND password is correct
      if (rememberMe) {
        localStorage.setItem(`profile_auth_${selectedProfile.id}`, "true");
        localStorage.setItem(
          `profile_auth_${selectedProfile.id}_expires`,
          (Date.now() + 30 * 24 * 60 * 60 * 1000).toString(),
        );
      } else {
        // Clear any existing remember me data
        localStorage.removeItem(`profile_auth_${selectedProfile.id}`);
        localStorage.removeItem(`profile_auth_${selectedProfile.id}_expires`);
      }
      onProfileUnlocked(selectedProfile);
    } else {
      setError("Incorrect password");
    }
  };

  const handleCancel = () => {
    setSelectedProfile(null);
    setError("");
  };

  if (loading) {
    return (
      <div className="profile-selector">
        <div className="profile-selector__title">Loading...</div>
      </div>
    );
  }

  return (
    <div className="profile-selector">
      <div className="profile-selector__title">Who's drawing?</div>
      <div className="profile-selector__profiles">
        {profiles.map((profile) => (
          <ProfileCard
            key={profile.id}
            profile={profile}
            onSelect={handleProfileSelect}
          />
        ))}
      </div>

      {selectedProfile && (
        <ProfileLockDialog
          profile={selectedProfile}
          onUnlock={handleUnlock}
          onCancel={handleCancel}
          error={error}
        />
      )}
    </div>
  );
};
